import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { Check, CaretUpDown, Plus } from "@phosphor-icons/react";
import { cn } from "../lib/utils";

/**
 * Searchable client combobox.
 * Props:
 *  - clients: [{ id, full_name, email, company }]
 *  - value: string | "__new" | ""
 *  - onChange: (id) => void
 *  - allowNew: boolean (include "+ Nouveau client" option)
 */
export default function ClientCombobox({ clients = [], value, onChange, allowNew = true }) {
  const [open, setOpen] = useState(false);

  const selected = value && value !== "__new" ? clients.find((c) => c.id === value) : null;

  const label = value === "__new"
    ? "+ Nouveau client (par e-mail)"
    : selected
      ? `${selected.full_name} · ${selected.email}`
      : "Rechercher un client…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="client-combobox-trigger"
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <span className="truncate">{label}</span>
          <CaretUpDown size={16} className="ml-2 shrink-0 opacity-60"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue is what we set on <CommandItem value=...>
            if (itemValue.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Chercher par nom, e-mail, société…" data-testid="client-combobox-search"/>
          <CommandList>
            <CommandEmpty>Aucun client trouvé.</CommandEmpty>
            {allowNew && (
              <CommandGroup heading="Créer">
                <CommandItem
                  value="__new nouveau client par email"
                  onSelect={() => { onChange("__new"); setOpen(false); }}
                  data-testid="client-option-new"
                >
                  <Plus size={14} className="mr-2"/>
                  Nouveau client (par e-mail)
                </CommandItem>
              </CommandGroup>
            )}
            {clients.length > 0 && (
              <CommandGroup heading="Clients existants">
                {clients.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`${c.full_name} ${c.email} ${c.company || ""}`}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                    data-testid={`client-option-${c.id}`}
                  >
                    <Check
                      size={14}
                      className={cn("mr-2", value === c.id ? "opacity-100" : "opacity-0")}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{c.full_name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {c.email}{c.company ? ` · ${c.company}` : ""}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
